import { z } from 'zod';
import { DisbursementMode, PayrollRunStatus } from '../enums';
import { IFSC_REGEX } from '../constants';
import { booleanish, dateSchema, paginationSchema, zEnum } from './common';
import { percentOfPaise, toPaise, toRupees } from './billing';

// ---------------------------------------------------------------------------
// Statutory rates
//
// These are the figures the law fixes, gathered in one place so that when a
// budget moves one of them there is exactly one line to change and a test that
// fails if it is changed carelessly. Every rate here is applied to *earned*
// wages — what the worker actually accrued this month — not to the agreed
// monthly figure, because a person who worked 12 days did not earn a month.
//
// Money is handled exactly as it is on the billing side: integer paise
// throughout, converted to rupees once at the edge. A payslip that disagrees
// with the sum of its own lines is a payslip a worker brings back to the desk.
// ---------------------------------------------------------------------------

export const STATUTORY = {
  pf: {
    /** EPF & MP Act 1952. Employee and employer each contribute 12%. */
    employeeRate: 12,
    employerRate: 12,
    /**
     * Of the employer's 12%, this much goes to the Pension Scheme and the
     * remainder to the Provident Fund. EPS is itself capped at the wage ceiling
     * even when PF is being paid on a higher wage.
     */
    pensionRate: 8.33,
    /** Employees' Deposit Linked Insurance, paid by the employer. */
    edliRate: 0.5,
    /** EPFO administrative charges on PF wages, paid by the employer. */
    adminRate: 0.5,
    /**
     * The statutory monthly wage ceiling. Contributions may be restricted to
     * this figure, which is what most contract-labour employers do — see
     * `restrictToCeiling`.
     */
    wageCeiling: 15_000,
    /** A minimum admin charge applies per establishment per month, not per member. */
    minAdminChargePerEstablishment: 75,
  },
  esi: {
    /** ESI Act 1948, rates effective from 01-07-2019. */
    employeeRate: 0.75,
    employerRate: 3.25,
    /**
     * Coverage ceiling on monthly gross. Tested *excluding* overtime — a month
     * of heavy overtime must not throw a covered worker out of the scheme —
     * while contributions are then charged *including* it.
     */
    wageCeiling: 21_000,
  },
  bonus: {
    /** Payment of Bonus Act 1965: 8.33% minimum, 20% maximum. */
    minRate: 8.33,
    /** Bonus is computed on wages capped at this figure. */
    wageCap: 7_000,
    /** Only workers earning at or below this are eligible. */
    eligibilityCeiling: 21_000,
  },
  gratuity: {
    /**
     * 15 days' wages for every completed year, on a 26-day month, is 4.81% of
     * basic. Accrued monthly so the liability is visible before it is payable;
     * it vests only after five years of continuous service.
     */
    accrualRate: 4.81,
    vestingYears: 5,
  },
  /**
   * Tamil Nadu Labour Welfare Fund — a flat annual contribution, collected with
   * December's wages.
   */
  lwf: {
    employee: 20,
    employer: 40,
    /** 1-based calendar month in which it is deducted. */
    deductionMonth: 12,
  },
} as const;

/**
 * Professional tax, Tamil Nadu.
 *
 * TN levies PT half-yearly on half-yearly income, through the local body, not
 * monthly through the state. Payroll still has to hold a twelfth of it back
 * each month or the worker meets a lump sum in September and March, so the
 * slab is resolved on annualised-half-yearly wages and divided by six.
 *
 * The slabs are the Greater Chennai Corporation schedule, which the other TN
 * corporations follow. A different state means a different table — hence the
 * lookup rather than a hard-coded set of thresholds.
 */
export const PROFESSIONAL_TAX_SLABS: Record<
  string,
  { upTo: number; halfYearly: number }[]
> = {
  'tamil nadu': [
    { upTo: 21_000, halfYearly: 0 },
    { upTo: 30_000, halfYearly: 135 },
    { upTo: 45_000, halfYearly: 315 },
    { upTo: 60_000, halfYearly: 690 },
    { upTo: 75_000, halfYearly: 1_025 },
    { upTo: Infinity, halfYearly: 1_250 },
  ],
};

/** States with no professional tax at all, so an empty table is correct. */
export const NO_PROFESSIONAL_TAX_STATES = [
  'delhi',
  'haryana',
  'uttar pradesh',
  'rajasthan',
  'punjab',
  'goa',
  'himachal pradesh',
  'uttarakhand',
];

// ---------------------------------------------------------------------------
// Wage structure
// ---------------------------------------------------------------------------

export interface WageStructureOptions {
  /** Basic as a percentage of the agreed monthly gross. */
  basicPercent?: number;
  /** HRA as a percentage of basic. */
  hraPercent?: number;
}

export const DEFAULT_WAGE_STRUCTURE = {
  /**
   * 50% is the conventional split for contract manufacturing labour and is what
   * the Code on Wages contemplates — allowances above half of total remuneration
   * are pulled back into "wages" anyway, so a lower basic buys nothing.
   */
  basicPercent: 50,
  /** 40% of basic is the non-metro HRA convention. */
  hraPercent: 40,
} as const;

/**
 * What a worker will actually be handed, for an advertised monthly wage.
 *
 * This is NOT a promise and the caller must never render it as one. It is the
 * statutory arithmetic — the same rates `computePayslip` runs — applied to a
 * single stated assumption: that basic is `DEFAULT_WAGE_STRUCTURE.basicPercent`
 * of gross, which is this platform's own default and what payroll actually
 * computes with. A different employer split moves the PF line.
 *
 * Only the two deductions that apply to every covered worker are modelled.
 * Professional tax is a state slab, LWF is annual and lands in one month, and
 * income tax is nil at these wages — including any of them would trade a
 * defensible floor for a false precision.
 *
 * Returns null when there is nothing honest to say: no wage, or a period other
 * than monthly, where the deduction rules are not a simple percentage of the
 * advertised figure.
 */
export interface TakeHomeEstimate {
  /** The monthly gross the estimate was computed from. */
  gross: number;
  /** Employee PF at 12% of basic, restricted to the statutory wage ceiling. */
  pf: number;
  /** Employee ESI at 0.75% of gross, nil above the coverage ceiling. */
  esi: number;
  /** gross − pf − esi, rounded to the rupee. */
  net: number;
  /** The basic split this assumed, so the caller can state it. */
  assumedBasicPercent: number;
  /** True when the worker earns above the ESI coverage ceiling. */
  aboveEsiCeiling: boolean;
}

export const estimateTakeHome = (
  gross: number | null | undefined,
  period: string = 'MONTHLY',
): TakeHomeEstimate | null => {
  if (gross == null || !Number.isFinite(gross) || gross <= 0) return null;
  // Daily and hourly wages are advertised per unit worked; PF and ESI are
  // monthly concepts, and guessing a month's earnings from a day rate would
  // invent the number this function exists to avoid inventing.
  if (period !== 'MONTHLY') return null;

  const basic = (gross * DEFAULT_WAGE_STRUCTURE.basicPercent) / 100;
  // Restricted to the ceiling, which is what contract-labour employers do and
  // what this platform's own runs default to (`pfOnFullWages: false`).
  const pfWages = Math.min(basic, STATUTORY.pf.wageCeiling);
  const pf = Math.round((pfWages * STATUTORY.pf.employeeRate) / 100);

  const aboveEsiCeiling = gross > STATUTORY.esi.wageCeiling;
  const esi = aboveEsiCeiling ? 0 : Math.round((gross * STATUTORY.esi.employeeRate) / 100);

  return {
    gross: Math.round(gross),
    pf,
    esi,
    net: Math.round(gross) - pf - esi,
    assumedBasicPercent: DEFAULT_WAGE_STRUCTURE.basicPercent,
    aboveEsiCeiling,
  };
};

export interface WageStructure {
  basic: number;
  hra: number;
  /** Whatever is left of the agreed gross once basic and HRA are taken out. */
  otherAllowance: number;
  gross: number;
}

/**
 * Splits an agreed monthly wage into its components.
 *
 * A deployment records one figure — what the worker is paid a month — but PF is
 * charged on basic, HRA is exempt to a limit and a payslip has to show the
 * split. Rather than ask an operations team to key four numbers per worker, the
 * split is derived from one and can be overridden per company later.
 *
 * The remainder is deliberately given to `otherAllowance` rather than
 * distributed: it absorbs the rounding so basic + HRA + allowance is exactly
 * the agreed gross, every time.
 */
export const splitWageStructure = (
  monthlyGross: number,
  options: WageStructureOptions = {},
): WageStructure => {
  const grossPaise = toPaise(monthlyGross);
  const basicPaise = percentOfPaise(
    grossPaise,
    options.basicPercent ?? DEFAULT_WAGE_STRUCTURE.basicPercent,
  );
  const hraPaise = percentOfPaise(
    basicPaise,
    options.hraPercent ?? DEFAULT_WAGE_STRUCTURE.hraPercent,
  );

  return {
    basic: toRupees(basicPaise),
    hra: toRupees(hraPaise),
    otherAllowance: toRupees(grossPaise - basicPaise - hraPaise),
    gross: toRupees(grossPaise),
  };
};

/**
 * Pro-rates a full month's component by days actually payable.
 *
 * The divisor is calendar days, matching the invoice: weekly offs and holidays
 * are themselves payable days, so a person present every working day of a
 * 31-day month earns the whole month, not 26/31 of it.
 */
export const proRate = (fullMonthAmount: number, payableDays: number, daysInMonth: number): number =>
  daysInMonth > 0
    ? toRupees(Math.round((toPaise(fullMonthAmount) * payableDays) / daysInMonth))
    : 0;

// ---------------------------------------------------------------------------
// Provident fund
// ---------------------------------------------------------------------------

export interface PfOptions {
  /**
   * Cap PF wages at the statutory ceiling. Default true — the near-universal
   * choice for contract labour, and the assumption the client's own PF budget
   * will have been built on. Setting it false pays PF on the full basic, which
   * is more generous and cannot be reversed for an existing member.
   */
  restrictToCeiling?: boolean;
  applicable?: boolean;
}

export interface ComputedPf {
  /** The wage the contributions were actually charged on. */
  pfWages: number;
  employee: number;
  /** Employer's share that lands in the provident fund proper. */
  employerPf: number;
  /** Employer's share that lands in the pension scheme. */
  pension: number;
  edli: number;
  adminCharges: number;
  /** Everything the employer pays on top of the wage. */
  employerTotal: number;
}

/** EPFO returns are filed in whole rupees, so each contribution rounds to one. */
const toWholeRupees = (paise: number): number => Math.round(paise / 100) * 100;

/** ESIC requires each contribution to be rounded *up* to the next rupee. */
const ceilToWholeRupees = (paise: number): number => Math.ceil(paise / 100) * 100;

/**
 * Works out both sides of the provident fund.
 *
 * The pension share is capped at the ceiling independently of the fund share:
 * an employer paying PF on a basic above ₹15,000 still only pays EPS on
 * ₹15,000, and the difference falls into the provident fund rather than
 * disappearing. Getting that wrong understates the employer's cost and
 * overstates what reaches the pension account.
 */
export const computePf = (
  earnedBasicPlusDa: number,
  options: PfOptions = {},
): ComputedPf => {
  if (options.applicable === false) {
    return {
      pfWages: 0,
      employee: 0,
      employerPf: 0,
      pension: 0,
      edli: 0,
      adminCharges: 0,
      employerTotal: 0,
    };
  }

  const earnedPaise = toPaise(earnedBasicPlusDa);
  const ceilingPaise = toPaise(STATUTORY.pf.wageCeiling);
  const restrict = options.restrictToCeiling ?? true;

  const pfWagesPaise = restrict ? Math.min(earnedPaise, ceilingPaise) : earnedPaise;
  // EPS is always capped, whether or not PF itself is.
  const pensionWagesPaise = Math.min(pfWagesPaise, ceilingPaise);

  const employee = toWholeRupees(percentOfPaise(pfWagesPaise, STATUTORY.pf.employeeRate));
  const pension = toWholeRupees(percentOfPaise(pensionWagesPaise, STATUTORY.pf.pensionRate));
  // Derived by subtraction, not computed at 3.67%: the two halves must add back
  // to the employer's 12% exactly, and two independent roundings would not.
  const employerShare = toWholeRupees(percentOfPaise(pfWagesPaise, STATUTORY.pf.employerRate));
  const employerPf = employerShare - pension;

  const edli = toWholeRupees(percentOfPaise(pensionWagesPaise, STATUTORY.pf.edliRate));
  const adminCharges = toWholeRupees(percentOfPaise(pfWagesPaise, STATUTORY.pf.adminRate));

  return {
    pfWages: toRupees(pfWagesPaise),
    employee: toRupees(employee),
    employerPf: toRupees(employerPf),
    pension: toRupees(pension),
    edli: toRupees(edli),
    adminCharges: toRupees(adminCharges),
    employerTotal: toRupees(employerShare + edli + adminCharges),
  };
};

// ---------------------------------------------------------------------------
// Employees' State Insurance
// ---------------------------------------------------------------------------

export interface ComputedEsi {
  applicable: boolean;
  esiWages: number;
  employee: number;
  employer: number;
}

/**
 * Works out both sides of ESI.
 *
 * Two different wages are in play and conflating them is the classic error:
 * eligibility is tested on the agreed monthly gross **excluding overtime**, so
 * a burst of overtime cannot push a covered worker out of the scheme; the
 * contribution is then charged on everything actually earned **including**
 * overtime. Both are ESIC's rules, and they pull in opposite directions on
 * purpose — the scheme is meant to be hard to fall out of.
 *
 * A real contribution period runs April–September and October–March, and a
 * worker who crosses the ceiling mid-period keeps contributing until the period
 * ends. That needs a member's history rather than one month's figures, so it is
 * not decided here; `applicable` may be forced by the caller once that history
 * exists.
 */
export const computeEsi = (
  input: {
    /** Agreed monthly gross, overtime excluded — the coverage test. */
    monthlyGrossForCoverage: number;
    /** Everything earned this month, overtime included — the contribution base. */
    earnedGrossIncludingOvertime: number;
  },
  options: { applicable?: boolean } = {},
): ComputedEsi => {
  const covered =
    options.applicable ??
    toPaise(input.monthlyGrossForCoverage) <= toPaise(STATUTORY.esi.wageCeiling);

  if (!covered) {
    return { applicable: false, esiWages: 0, employee: 0, employer: 0 };
  }

  const wagesPaise = toPaise(input.earnedGrossIncludingOvertime);
  return {
    applicable: true,
    esiWages: toRupees(wagesPaise),
    employee: toRupees(ceilToWholeRupees(percentOfPaise(wagesPaise, STATUTORY.esi.employeeRate))),
    employer: toRupees(ceilToWholeRupees(percentOfPaise(wagesPaise, STATUTORY.esi.employerRate))),
  };
};

// ---------------------------------------------------------------------------
// Professional tax
// ---------------------------------------------------------------------------

/**
 * The monthly professional-tax deduction for a state.
 *
 * Resolved on half-yearly wages (six months of the agreed gross) because that
 * is the base the TN schedule is written against, then divided by six so the
 * worker meets it in twelfths rather than as a lump sum. Rounded to a whole
 * rupee; the twelfths therefore need not sum exactly to the half-yearly figure,
 * and the difference is settled when the challan is filed.
 */
export const computeProfessionalTax = (monthlyGross: number, state: string): number => {
  const key = state.trim().toLowerCase();
  if (NO_PROFESSIONAL_TAX_STATES.includes(key)) return 0;

  const slabs = PROFESSIONAL_TAX_SLABS[key];
  if (!slabs?.length) return 0;

  const halfYearlyWages = toRupees(toPaise(monthlyGross) * 6);
  // The table's last entry is open-ended, so `find` only misses if a table is
  // ever written without one; falling back to nil beats guessing a rate.
  const slab = slabs.find((s) => halfYearlyWages <= s.upTo);
  if (!slab) return 0;

  return toRupees(Math.round(toPaise(slab.halfYearly) / 6));
};

// ---------------------------------------------------------------------------
// Accruals — costs incurred this month but paid later
// ---------------------------------------------------------------------------

/**
 * Statutory minimum bonus, accrued monthly.
 *
 * Payable annually, but a manpower business that only discovers the liability
 * in October has already quoted a year of margins without it. Computed on wages
 * capped at ₹7,000, and only for workers under the eligibility ceiling.
 */
export const computeBonusAccrual = (earnedBasicPlusDa: number, monthlyGross: number): number => {
  if (toPaise(monthlyGross) > toPaise(STATUTORY.bonus.eligibilityCeiling)) return 0;
  const cappedPaise = Math.min(toPaise(earnedBasicPlusDa), toPaise(STATUTORY.bonus.wageCap));
  return toRupees(percentOfPaise(cappedPaise, STATUTORY.bonus.minRate));
};

/**
 * Gratuity accrued this month.
 *
 * Vests only after five years, and most contract deployments end well before
 * that — but the ones that do not are the expensive ones, and the accrual is
 * how the liability is visible before it lands.
 */
export const computeGratuityAccrual = (earnedBasic: number): number =>
  toRupees(percentOfPaise(toPaise(earnedBasic), STATUTORY.gratuity.accrualRate));

// ---------------------------------------------------------------------------
// The payslip
// ---------------------------------------------------------------------------

export interface PayslipInput {
  /** The agreed monthly wage, as recorded on the deployment. */
  monthlyGross: number;
  /** Payable days from the muster — already accounts for half days. */
  payableDays: number;
  daysInMonth: number;
  overtimeHours?: number;
  /**
   * Per-hour overtime rate. Left to the caller so a contract that pays a flat
   * rate can, but it defaults to the statutory double rate — see
   * `statutoryOvertimeRate`.
   */
  overtimeRate?: number;
  /** Incentives, attendance bonus, night allowance — earned, taxable, non-statutory. */
  otherEarnings?: number;
  /** Advances, canteen, uniform recovery. */
  otherDeductions?: number;
  /** Decides the professional-tax table. */
  state: string;
  /** 1-based; the Labour Welfare Fund is deducted once a year, in December. */
  month: number;
  structure?: WageStructureOptions;
  pf?: PfOptions;
  esi?: { applicable?: boolean };
  /** Skip the LWF line for a state that does not operate a fund. */
  lwfApplicable?: boolean;
}

export interface ComputedPayslip {
  /** The full-month split, shown on the payslip beside what was earned. */
  structure: WageStructure;
  earned: {
    basic: number;
    hra: number;
    otherAllowance: number;
    overtimeAmount: number;
    otherEarnings: number;
    /** Everything owed before a single deduction. */
    grossEarnings: number;
  };
  deductions: {
    pf: number;
    esi: number;
    professionalTax: number;
    lwf: number;
    other: number;
    total: number;
    /**
     * Recovery that would have pushed net pay below zero and was held back.
     *
     * An advance larger than the month's wages is recovered over more than one
     * month; it is not taken out of a worker's pocket, and a bank file
     * containing a negative amount is rejected outright.
     */
    carriedForward: number;
  };
  employerCost: {
    pf: number;
    pension: number;
    edli: number;
    pfAdmin: number;
    esi: number;
    bonusAccrual: number;
    gratuityAccrual: number;
    lwf: number;
    total: number;
  };
  /** Gross less deductions, to the paisa. */
  netPayable: number;
  /** Between -0.50 and +0.50 — wages are disbursed in whole rupees. */
  roundOff: number;
  /** What actually reaches the worker's account. */
  netPaid: number;
  /**
   * Gross earnings plus every employer-borne cost. The figure to hold the
   * client's billing rate against when asking whether a deployment makes money.
   */
  costToCompany: number;
  pfApplicable: boolean;
  esiApplicable: boolean;
}

/**
 * The statutory overtime rate: twice the ordinary rate of wages.
 *
 * Section 59 of the Factories Act, and section 14 of the Minimum Wages Act say
 * the same thing. Worth noticing that the billing side charges the client
 * overtime at a single rate of the *billing* rate: whether that covers a double
 * rate on the *wage* depends on the margin, and on a thin one it will not.
 */
export const statutoryOvertimeRate = (monthlyGross: number, daysInMonth: number): number =>
  daysInMonth > 0 ? toRupees(Math.round((toPaise(monthlyGross) * 2) / daysInMonth / 8)) : 0;

/**
 * Turns one worker's month into a payslip.
 *
 * Pure arithmetic over facts the caller has already established — the muster is
 * locked, the wage is agreed — so it can be tested without a database and gives
 * the screen and the server the same answer.
 */
export const computePayslip = (input: PayslipInput): ComputedPayslip => {
  const structure = splitWageStructure(input.monthlyGross, input.structure);

  const earnedBasic = proRate(structure.basic, input.payableDays, input.daysInMonth);
  const earnedHra = proRate(structure.hra, input.payableDays, input.daysInMonth);
  const earnedOther = proRate(structure.otherAllowance, input.payableDays, input.daysInMonth);

  const overtimeRate =
    input.overtimeRate ?? statutoryOvertimeRate(input.monthlyGross, input.daysInMonth);
  const overtimeAmount = toRupees(
    Math.round(toPaise(overtimeRate) * (input.overtimeHours ?? 0)),
  );

  const otherEarnings = toRupees(toPaise(input.otherEarnings ?? 0));

  // Summed in paise. Adding five components as floats is how a payslip ends up
  // a paisa away from the sum of its own lines.
  const grossEarningsPaise =
    toPaise(earnedBasic) +
    toPaise(earnedHra) +
    toPaise(earnedOther) +
    toPaise(overtimeAmount) +
    toPaise(otherEarnings);
  const grossEarnings = toRupees(grossEarningsPaise);

  // PF is charged on basic + DA only. There is no separate DA here — it is
  // folded into basic — so basic is the PF wage.
  const pf = computePf(earnedBasic, input.pf);
  const esi = computeEsi(
    {
      // Overtime excluded from the coverage test, included in the contribution.
      monthlyGrossForCoverage: structure.gross,
      earnedGrossIncludingOvertime: grossEarnings,
    },
    input.esi ?? {},
  );

  // Professional tax and the welfare fund are flat charges, not proportions, so
  // unlike PF and ESI they do not fall to zero on their own when nothing was
  // earned. A worker absent all month owes neither — both are levied on wages
  // paid, and deducting them anyway is how a payslip reaches a negative net.
  const earnedAnything = grossEarningsPaise > 0;

  const professionalTax = earnedAnything
    ? computeProfessionalTax(structure.gross, input.state)
    : 0;

  const lwfMonth = input.month === STATUTORY.lwf.deductionMonth;
  const lwfApplicable = (input.lwfApplicable ?? true) && lwfMonth && earnedAnything;
  const lwfEmployee = lwfApplicable ? STATUTORY.lwf.employee : 0;
  const lwfEmployer = lwfApplicable ? STATUTORY.lwf.employer : 0;

  const statutoryDeductionsPaise =
    toPaise(pf.employee) +
    toPaise(esi.employee) +
    toPaise(professionalTax) +
    toPaise(lwfEmployee);

  // Recovery is taken only out of what is left after the statutory lines. An
  // advance bigger than the month's wages carries into the next run rather than
  // turning the payslip negative.
  const requestedOtherPaise = toPaise(input.otherDeductions ?? 0);
  const recoverablePaise = Math.max(0, grossEarningsPaise - statutoryDeductionsPaise);
  const otherPaise = Math.min(requestedOtherPaise, recoverablePaise);
  const carriedForwardPaise = requestedOtherPaise - otherPaise;

  const deductionsPaise = statutoryDeductionsPaise + otherPaise;

  const netPayablePaise = grossEarningsPaise - deductionsPaise;
  const netPaidPaise = Math.round(netPayablePaise / 100) * 100;

  const bonusAccrual = computeBonusAccrual(earnedBasic, structure.gross);
  const gratuityAccrual = computeGratuityAccrual(earnedBasic);

  const employerTotalPaise =
    toPaise(pf.employerTotal) +
    toPaise(esi.employer) +
    toPaise(bonusAccrual) +
    toPaise(gratuityAccrual) +
    toPaise(lwfEmployer);

  return {
    structure,
    earned: {
      basic: earnedBasic,
      hra: earnedHra,
      otherAllowance: earnedOther,
      overtimeAmount,
      otherEarnings,
      grossEarnings,
    },
    deductions: {
      pf: pf.employee,
      esi: esi.employee,
      professionalTax,
      lwf: lwfEmployee,
      other: toRupees(otherPaise),
      total: toRupees(deductionsPaise),
      carriedForward: toRupees(carriedForwardPaise),
    },
    employerCost: {
      pf: pf.employerPf,
      pension: pf.pension,
      edli: pf.edli,
      pfAdmin: pf.adminCharges,
      esi: esi.employer,
      bonusAccrual,
      gratuityAccrual,
      lwf: lwfEmployer,
      total: toRupees(employerTotalPaise),
    },
    netPayable: toRupees(netPayablePaise),
    roundOff: toRupees(netPaidPaise - netPayablePaise),
    netPaid: toRupees(netPaidPaise),
    costToCompany: toRupees(grossEarningsPaise + employerTotalPaise),
    pfApplicable: pf.pfWages > 0,
    esiApplicable: esi.applicable,
  };
};

// ---------------------------------------------------------------------------
// Run totals
// ---------------------------------------------------------------------------

export interface PayrollRunTotals {
  headcount: number;
  grossEarnings: number;
  totalDeductions: number;
  netPaid: number;
  employerPf: number;
  employerEsi: number;
  employerOther: number;
  employerTotal: number;
  costToCompany: number;
}

/** Rolls payslips into the figures a run header carries. Summed in paise. */
export const computeRunTotals = (
  slips: Pick<ComputedPayslip, 'earned' | 'deductions' | 'employerCost' | 'netPaid' | 'costToCompany'>[],
): PayrollRunTotals => {
  let gross = 0;
  let deductions = 0;
  let net = 0;
  let pf = 0;
  let esi = 0;
  let other = 0;
  let employer = 0;
  let ctc = 0;

  for (const s of slips) {
    gross += toPaise(s.earned.grossEarnings);
    deductions += toPaise(s.deductions.total);
    net += toPaise(s.netPaid);
    pf += toPaise(s.employerCost.pf) + toPaise(s.employerCost.pension) +
      toPaise(s.employerCost.edli) + toPaise(s.employerCost.pfAdmin);
    esi += toPaise(s.employerCost.esi);
    other += toPaise(s.employerCost.bonusAccrual) + toPaise(s.employerCost.gratuityAccrual) +
      toPaise(s.employerCost.lwf);
    employer += toPaise(s.employerCost.total);
    ctc += toPaise(s.costToCompany);
  }

  return {
    headcount: slips.length,
    grossEarnings: toRupees(gross),
    totalDeductions: toRupees(deductions),
    netPaid: toRupees(net),
    employerPf: toRupees(pf),
    employerEsi: toRupees(esi),
    employerOther: toRupees(other),
    employerTotal: toRupees(employer),
    costToCompany: toRupees(ctc),
  };
};

// ---------------------------------------------------------------------------
// The run state machine
//
// DRAFT → APPROVED → PAID, with a cancel from either open state. Kept here
// rather than in the API service for the same reason the attendance period
// machine is: the screen has to grey out the same buttons the server refuses,
// and two copies of that rule is one too many.
// ---------------------------------------------------------------------------

export type PayrollAction = 'approve' | 'markPaid' | 'cancel';

export type PayrollTransitionCheck =
  | { ok: true; next: PayrollRunStatus }
  | { ok: false; reason: string };

export const checkPayrollTransition = (
  current: PayrollRunStatus,
  action: PayrollAction,
  context: { reason?: string | null } = {},
): PayrollTransitionCheck => {
  if (action === 'cancel') {
    if (current === PayrollRunStatus.CANCELLED) {
      return { ok: false, reason: 'This run is already cancelled.' };
    }
    // A paid run means money has left the account. Cancelling it would strand
    // the disbursement with nothing to reconcile against.
    if (current === PayrollRunStatus.PAID) {
      return {
        ok: false,
        reason: 'Wages have already been disbursed. Correct this with an adjustment in the next run.',
      };
    }
    if (!context.reason?.trim()) {
      return { ok: false, reason: 'Give a reason for cancelling a payroll run.' };
    }
    return { ok: true, next: PayrollRunStatus.CANCELLED };
  }

  if (action === 'approve') {
    if (current !== PayrollRunStatus.DRAFT) {
      return { ok: false, reason: `This run is already ${current.toLowerCase()}.` };
    }
    return { ok: true, next: PayrollRunStatus.APPROVED };
  }

  if (current !== PayrollRunStatus.APPROVED) {
    return {
      ok: false,
      reason:
        current === PayrollRunStatus.DRAFT
          ? 'Approve the run before marking wages disbursed.'
          : `This run is ${current.toLowerCase()}.`,
    };
  }
  return { ok: true, next: PayrollRunStatus.PAID };
};

/** Only a draft may be recomputed; approved figures are what a worker was shown. */
export const isRunEditable = (status: PayrollRunStatus): boolean =>
  status === PayrollRunStatus.DRAFT;

/**
 * The run states whose payslips a worker may see.
 *
 * Whitelisted rather than excluding DRAFT, because the two states a worker must
 * not see fail differently: a draft is our working copy and might still change,
 * while a **cancelled** run is wages that were never paid at all. Showing the
 * latter tells someone they were paid money they never received, which is worse
 * than showing nothing.
 */
export const WORKER_VISIBLE_PAYROLL_STATUSES: PayrollRunStatus[] = [
  PayrollRunStatus.APPROVED,
  PayrollRunStatus.PAID,
];

/** Whether a run's payslips may be shown to the worker they belong to. */
export const isPayslipVisibleToWorker = (status: PayrollRunStatus): boolean =>
  WORKER_VISIBLE_PAYROLL_STATUSES.includes(status);

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export const generatePayrollSchema = z.object({
  companyId: z.string().uuid(),
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  /** The date wages are intended to reach accounts; defaults to today. */
  payDate: dateSchema.optional(),
  basicPercent: z.number().min(30).max(100).optional(),
  hraPercent: z.number().min(0).max(100).optional(),
  /** Pay PF on the full basic rather than capping at the statutory ceiling. */
  pfOnFullWages: z.boolean().default(false),
  notes: z.string().max(2000).optional(),
});
export type GeneratePayrollInput = z.infer<typeof generatePayrollSchema>;

export const payrollSearchSchema = paginationSchema.extend({
  companyId: z.string().uuid().optional(),
  status: zEnum(PayrollRunStatus).optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
  /** `booleanish`, not `z.coerce.boolean()`, which reads the string "false" as true. */
  unpaidOnly: booleanish.optional(),
});
export type PayrollSearchQuery = z.infer<typeof payrollSearchSchema>;

export const payrollActionSchema = z.object({
  runId: z.string().uuid(),
  /** Required on a cancel — it is the audit trail for why wages changed. */
  reason: z.string().trim().max(500).optional(),
});
export type PayrollActionInput = z.infer<typeof payrollActionSchema>;

export const markPayrollPaidSchema = z.object({
  runId: z.string().uuid(),
  paidAt: dateSchema,
  mode: zEnum(DisbursementMode).default(DisbursementMode.BANK_TRANSFER),
  reference: z.string().max(120).optional(),
  notes: z.string().max(1000).optional(),
});
export type MarkPayrollPaidInput = z.infer<typeof markPayrollPaidSchema>;

/**
 * Per-worker adjustments applied before a draft is approved: an advance to
 * recover, an incentive to add. Kept off the deployment because they are true
 * of one month, not of the posting.
 */
export const adjustPayslipSchema = z.object({
  payslipId: z.string().uuid(),
  otherEarnings: z.number().min(0).max(1_000_000).optional(),
  otherDeductions: z.number().min(0).max(1_000_000).optional(),
  remarks: z.string().trim().max(300).optional(),
});
export type AdjustPayslipInput = z.infer<typeof adjustPayslipSchema>;

export const payslipSearchSchema = paginationSchema.extend({
  runId: z.string().uuid().optional(),
  deploymentId: z.string().uuid().optional(),
  seekerId: z.string().uuid().optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
});
export type PayslipSearchQuery = z.infer<typeof payslipSearchSchema>;

// ---------------------------------------------------------------------------
// The bank disbursement file
//
// The last step in the chain: an approved run becomes a batch a bank will
// accept. Every bank publishes its own upload template, so what is produced
// here is the common set of columns all of them ask for — beneficiary, account,
// IFSC, amount, mode, narration — which an accounts team maps onto their bank's
// sheet once and then reuses.
// ---------------------------------------------------------------------------

/**
 * How a single row is to be remitted.
 *
 * RTGS is not a preference — it is the only rail available at or above ₹2 lakh,
 * and it is unavailable below it. NEFT carries everything else; salary batches
 * are almost entirely NEFT.
 */
export const BankTransferMode = {
  NEFT: 'NEFT',
  RTGS: 'RTGS',
} as const;
export type BankTransferMode = (typeof BankTransferMode)[keyof typeof BankTransferMode];

/** RTGS has a ₹2,00,000 floor; NEFT has none. */
export const RTGS_MINIMUM = 200_000;

export const bankTransferModeFor = (amount: number): BankTransferMode =>
  toPaise(amount) >= toPaise(RTGS_MINIMUM) ? BankTransferMode.RTGS : BankTransferMode.NEFT;

/**
 * IFSC: four letters for the bank, a reserved `0`, then six for the branch.
 *
 * Reuses the same `IFSC_REGEX` the seeker profile validates against on entry —
 * a second copy of the rule here would eventually accept something the profile
 * form rejects, or the reverse. Worth re-checking at this point anyway, because
 * a malformed IFSC fails the whole upload at most banks, not just its own row.
 */
export const isValidIfsc = (ifsc: string | null | undefined): boolean =>
  typeof ifsc === 'string' && IFSC_REGEX.test(ifsc.trim().toUpperCase());

/**
 * Indian bank account numbers run 9–18 digits.
 *
 * Same rule the profile form applies, restated here because the file is built
 * from data that may have been written before that rule existed.
 */
export const BANK_ACCOUNT_REGEX = /^\d{9,18}$/;

export const isValidBankAccount = (account: string | null | undefined): boolean =>
  typeof account === 'string' && BANK_ACCOUNT_REGEX.test(account.trim());

/** Most banks truncate the narration at 35 characters. */
export const BANK_NARRATION_MAX = 35;

/**
 * What the worker sees on their statement.
 *
 * Deliberately carries the month: "SALARY" alone tells someone nothing when two
 * runs land in the same week.
 */
export const buildBankNarration = (
  year: number,
  month: number,
  employeeCode?: string | null,
): string => {
  const stamp = `${['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'][month - 1]}${year}`;
  return `SALARY ${stamp}${employeeCode ? ` ${employeeCode}` : ''}`.slice(0, BANK_NARRATION_MAX);
};

/** The header row. Ordered as the common bank templates expect it. */
export const BANK_FILE_COLUMNS = [
  'Beneficiary Name',
  'Account Number',
  'IFSC',
  'Amount',
  'Mode',
  'Narration',
  'Employee Code',
] as const;

export interface BankRowCandidate {
  workerName: string;
  employeeCode?: string | null;
  /**
   * The account number, when the caller has it.
   *
   * The preview deliberately does not: it runs without the authority to decrypt
   * anything, so it passes `hasAccount` instead and the digits are re-checked
   * once the file itself decrypts them.
   */
  accountNumber?: string | null;
  /** Whether an account is on record at all. Used when the number is withheld. */
  hasAccount?: boolean;
  ifsc?: string | null;
  netPaid: number;
}

export type BankRowCheck =
  | { ok: true }
  | { ok: false; reason: string };

/**
 * Whether one worker can actually be paid by transfer.
 *
 * Pure, so the preview on screen and the file on the server answer identically —
 * a worker shown as payable who is then silently missing from the batch is
 * somebody who does not get paid on payday and finds out afterwards.
 */
export const checkBankRow = (row: BankRowCandidate): BankRowCheck => {
  const known = row.accountNumber?.trim();

  if (known) {
    if (!isValidBankAccount(known)) {
      return { ok: false, reason: 'Bank account number is not 9–18 digits' };
    }
  } else if (!row.hasAccount) {
    return { ok: false, reason: 'No bank account on record' };
  }
  if (!row.ifsc?.trim()) {
    return { ok: false, reason: 'No IFSC on record' };
  }
  if (!isValidIfsc(row.ifsc)) {
    return { ok: false, reason: `IFSC "${row.ifsc}" is malformed` };
  }
  // A zero-value row is rejected by the bank and means nothing is owed anyway.
  if (toPaise(row.netPaid) <= 0) {
    return { ok: false, reason: 'Nothing payable this month' };
  }
  return { ok: true };
};

/**
 * Renders rows as CSV.
 *
 * Amounts are plain decimals — no symbol, no thousands separator. A bank upload
 * rejects "₹14,237.00"; it wants "14237.00". This is the single most common
 * reason a batch bounces, which is why the formatting never goes through
 * `formatINR` here.
 */
export const toBankCsv = (rows: BankFileRow[]): string => {
  const escape = (value: string | number): string => {
    const text = String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };

  const lines = [BANK_FILE_COLUMNS.join(',')];
  for (const r of rows) {
    lines.push(
      [
        escape(r.beneficiaryName),
        // Written as raw digits, which is what a bank's upload parser reads and
        // what preserves a leading zero. Note that *Excel* will still strip that
        // zero if a human opens the .csv by hand — quoting does not stop it, and
        // the `="…"` trick that would breaks the bank parsers. Use the .xlsx
        // format for anything a person is going to open; its account column is
        // explicitly text-formatted.
        escape(r.accountNumber),
        escape(r.ifsc),
        r.amount.toFixed(2),
        escape(r.mode),
        escape(r.narration),
        escape(r.employeeCode ?? ''),
      ].join(','),
    );
  }
  // Trailing newline: some bank parsers drop the final record without one.
  return `${lines.join('\r\n')}\r\n`;
};

export interface BankFileRow {
  beneficiaryName: string;
  accountNumber: string;
  ifsc: string;
  amount: number;
  mode: BankTransferMode;
  narration: string;
  employeeCode?: string | null;
}

export const bankFileQuerySchema = z.object({
  format: z.enum(['csv', 'xlsx']).default('csv'),
  /**
   * Produce a file even though some workers cannot be paid.
   *
   * Off by default: a batch that quietly omits three people looks exactly like a
   * complete one, and nobody notices until those three ask where their wages
   * are. Turning it on is recorded on the audit trail.
   */
  allowPartial: booleanish.default(false),
});
export type BankFileQuery = z.infer<typeof bankFileQuerySchema>;

/** A run's wages may only be sent to a bank once somebody has approved them. */
export const isDisbursable = (status: PayrollRunStatus): boolean =>
  status === PayrollRunStatus.APPROVED || status === PayrollRunStatus.PAID;

/** Statutory filings a month produces: the PF ECR and the ESI return. */
export const statutoryReportSchema = z.object({
  companyId: z.string().uuid().optional(),
  year: z.coerce.number().int().min(2000).max(2100),
  month: z.coerce.number().int().min(1).max(12),
});
export type StatutoryReportQuery = z.infer<typeof statutoryReportSchema>;
